import React, { useEffect, useState } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Zap, Link2, Copy, RefreshCw, CheckCircle2, Euro, Webhook, KeyRound, ShieldCheck, User, Info, ChevronDown } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import PageHeader from '../../components/PageHeader';
import SaleAttributionExample from '../../components/SaleAttributionExample';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import api, { apiError, fmtDateTime } from '../../lib/api';

// Vente faite sur le lien du bot d'accueil (même règle que la page Ventes) : créditée au Bot, jamais à un chatteur.
const estVenteBot = (s) => String(s.reason || '').startsWith('auto:bot_');

export default function DropFans() {
  const { t } = useTranslation();
  const [config, setConfig] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [sales, setSales] = useState([]);
  const [chatters, setChatters] = useState([]);
  const [links, setLinks] = useState([]);
  const [deliveries, setDeliveries] = useState([]);
  const [apiKey, setApiKey] = useState('');
  const [secret, setSecret] = useState('');
  const [enabling, setEnabling] = useState(null);
  const [importing, setImporting] = useState(false);
  const [lastImport, setLastImport] = useState(null);
  const [showGuide, setShowGuide] = useState(false);

  const importHistory = async () => {
    if (importing) return;
    setImporting(true);
    try {
      const { data } = await api.post('/dropfans/import-history');
      setLastImport(data);
      const unmatchedNames = Object.keys(data.unmatched || {});
      toast.success(t('dash.dropfans.importToast', { imported: data.imported, eur: Math.round(data.imported_eur || 0).toLocaleString(), already: data.already_present }));
      if (unmatchedNames.length > 0) {
        toast.warning(t('dash.dropfans.importUnmatched', { count: unmatchedNames.length }), { duration: 9000 });
      }
      load();
    } catch (e) { toast.error(apiError(e, t('dash.dropfans.importFailed'))); } finally { setImporting(false); }
  };

  const load = async () => {
    try {
      const [c, a, s] = await Promise.all([api.get('/dropfans/config'), api.get('/accounts'), api.get('/sales')]);
      const cfg = c.data || {};
      setConfig(cfg);
      setApiKey(cfg.api_key || '');
      setSecret(cfg.webhook_secret || '');
      setAccounts(a.data || []);
      setSales(s.data || []);
    } catch (e) { toast.error(apiError(e, t('dash.dropfans.loadFailed'))); }
    api.get('/dropfans/links').then(({ data }) => setLinks(data)).catch(() => {});
    api.get('/webhook-deliveries').then(({ data }) => setDeliveries(data)).catch(() => {});
    api.get('/chatters').then(({ data }) => setChatters(data || [])).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const patchConfig = async (patch, msg) => {
    try {
      const { data } = await api.patch('/dropfans/config', patch);
      setConfig(data);
      toast.success(msg || t('dash.dropfans.configSaved'));
    } catch (e) { toast.error(apiError(e, t('dash.dropfans.saveFailed'))); }
  };

  // Manually (re)assign a sale to a chatter — chatter_id "" clears it back to
  // "à attribuer". Backend (PATCH /sales) updates the credit; we reload to reflect.
  const reassignSale = async (saleId, chatterId) => {
    try {
      await api.patch(`/sales/${saleId}`, { chatter_id: chatterId });
      toast.success(chatterId ? 'Vente réattribuée' : 'Vente remise « à attribuer »');
      load();
    } catch (e) { toast.error(apiError(e, 'Échec de la réattribution')); }
  };

  const webhookUrl = `${process.env.REACT_APP_BACKEND_URL}/api/dropfans/webhook`;
  const connected = accounts.filter(a => a.dropfans_enabled);
  const autoSales = sales.filter(s => s.source === 'auto');
  const revenue = sales.filter(s => s.status === 'approved').reduce((s, x) => s + x.amount, 0);
  const attributed = sales.filter(s => s.chatter_id || estVenteBot(s)).length;   // vente du bot = déjà attribuée (au Bot)

  const copy = (v) => { navigator.clipboard?.writeText(v); toast.success(t('dash.dropfans.copied')); };

  return (
    <div className="p-6 md:p-8 max-w-7xl mx-auto fade-in" data-testid="dropfans-page">
      <PageHeader
        title={t('dash.dropfans.title')}
        subtitle={t('dash.dropfans.subtitle')}
        badge={<Badge className="bg-amber-500/15 text-amber-300 border-amber-500/30"><Zap size={11} className="mr-1" /> {config?.api_key ? t('dash.dropfans.connected') : t('dash.dropfans.notConfigured')}</Badge>}
        onRefresh={load}
        actions={
          <Button data-testid="dropfans-import-history" onClick={importHistory} disabled={importing || !config?.api_key}
            className="bg-amber-500/15 text-amber-300 border border-amber-500/30 hover:bg-amber-500/25 h-9">
            {importing ? <RefreshCw size={14} className="mr-1.5 animate-spin" /> : <Euro size={14} className="mr-1.5" />}
            {importing ? t('dash.dropfans.importing') : t('dash.dropfans.importHistory')}
          </Button>
        }
      />

      <div className="rounded-2xl border border-indigo-500/25 bg-indigo-500/[0.06] mb-6" data-testid="dropfans-guide">
        <button onClick={() => setShowGuide(v => !v)} className="w-full flex items-center gap-2 px-4 py-3 text-left">
          <Info size={15} className="text-indigo-300 shrink-0" />
          <span className="text-sm font-medium text-white">{t('dash.dropfans.guideToggle')}</span>
          <ChevronDown size={15} className={`ml-auto text-slate-400 transition-transform ${showGuide ? 'rotate-180' : ''}`} />
        </button>
        {showGuide && (
          <div className="px-4 pb-4 pt-0 space-y-4 text-sm text-slate-300">
            <div>
              <p className="text-white font-semibold mb-1.5">{t('dash.dropfans.guideAHeading')}</p>
              <ol className="list-decimal pl-5 space-y-2">
                <li dangerouslySetInnerHTML={{ __html: t('dash.dropfans.guideA1') }} />
                <li dangerouslySetInnerHTML={{ __html: t('dash.dropfans.guideA2') }} />
              </ol>
            </div>
            <div>
              <p className="text-white font-semibold mb-1.5">{t('dash.dropfans.guideBHeading')}</p>
              <ol className="list-decimal pl-5 space-y-2" start="3">
                <li dangerouslySetInnerHTML={{ __html: t('dash.dropfans.guideB3') }} />
                <li dangerouslySetInnerHTML={{ __html: t('dash.dropfans.guideB4') }} />
                <li dangerouslySetInnerHTML={{ __html: t('dash.dropfans.guideB5') }} />
              </ol>
            </div>
            <p className="text-[12px] text-slate-500" dangerouslySetInnerHTML={{ __html: t('dash.dropfans.guideTip') }} />
          </div>
        )}
      </div>

      {lastImport && (
        <div data-testid="import-result-card" className="rounded-2xl border border-amber-500/25 bg-amber-500/[0.06] p-4 mb-6">
          <div className="flex items-start justify-between gap-3">
            <div className="text-sm text-white">
              <span className="font-semibold text-emerald-300">{lastImport.imported}</span> {t('dash.dropfans.resultImported')}
              {' · '}<span className="font-semibold text-emerald-300">{Math.round(lastImport.imported_eur || 0).toLocaleString()} €</span>
              <span className="text-slate-400">{t('dash.dropfans.resultAlready', { count: lastImport.already_present })}</span>
            </div>
            <button onClick={() => setLastImport(null)} className="text-xs text-slate-400 hover:text-white shrink-0">{t('dash.dropfans.close')}</button>
          </div>
          {Object.keys(lastImport.unmatched || {}).length > 0 && (
            <div className="mt-3 border-t border-amber-500/20 pt-3">
              <p className="text-xs text-amber-200 mb-2" dangerouslySetInnerHTML={{ __html: t('dash.dropfans.resultUnmatchedNote') }} />
              <div className="space-y-1.5">
                {Object.entries(lastImport.unmatched).map(([name, count]) => {
                  const keys = (lastImport.unmatched_keys || {})[name] || {};
                  const eur = (lastImport.unmatched_eur || {})[name] || 0;
                  return (
                    <div key={name} className="flex items-center justify-between gap-3 rounded-lg bg-black/20 px-3 py-2 text-sm">
                      <span className="text-white font-medium">{name}</span>
                      <span className="text-slate-400 text-xs">{t('dash.dropfans.resultSalesCount', { count })} · <span className="text-emerald-300 font-semibold">{Math.round(eur).toLocaleString()} €</span></span>
                      <span className="ml-auto text-xs text-slate-300">{t('dash.dropfans.resultUsernameToEnter')}<code className="text-amber-300 font-mono bg-black/30 px-1.5 py-0.5 rounded">{keys.username || keys.id || '—'}</code></span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <Kpi testid="kpi-revenue" icon={Euro} color="text-emerald-400" label={t('dash.dropfans.kpiRevenue')} value={`${revenue.toLocaleString()} €`} />
        <Kpi testid="kpi-connected" icon={CheckCircle2} color="text-indigo-400" label={t('dash.dropfans.kpiConnected')} value={`${connected.length}/${accounts.length}`} />
        <Kpi testid="kpi-auto" icon={Zap} color="text-amber-400" label={t('dash.dropfans.kpiAuto')} value={`${autoSales.length}`} />
        <Kpi testid="kpi-links" icon={Link2} color="text-violet-400" label={t('dash.dropfans.kpiLinks')} value={`${links.length}`} />
      </div>

      <Tabs defaultValue="accounts" className="space-y-4">
        <TabsList className="bg-white/5 border border-white/10">
          <TabsTrigger data-testid="df-tab-accounts" value="accounts" className="data-[state=active]:bg-white/10">{t('dash.dropfans.tabAccounts')}</TabsTrigger>
          <TabsTrigger data-testid="df-tab-api" value="api" className="data-[state=active]:bg-white/10">{t('dash.dropfans.tabApi')}</TabsTrigger>
          <TabsTrigger data-testid="df-tab-attribution" value="attribution" className="data-[state=active]:bg-white/10">{t('dash.dropfans.tabAttribution')}</TabsTrigger>
          <TabsTrigger data-testid="df-tab-links" value="links" className="data-[state=active]:bg-white/10">{t('dash.dropfans.tabLinks')}</TabsTrigger>
        </TabsList>

        <TabsContent value="accounts">
          <div className="rounded-2xl border border-white/10 bg-white/[0.02] overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-white/5">
                  <th className="px-5 py-3 font-semibold">{t('dash.dropfans.thTelegramAccount')}</th>
                  <th className="px-5 py-3 font-semibold">{t('dash.dropfans.thDropfansProfile')}</th>
                  <th className="px-5 py-3 font-semibold">{t('dash.dropfans.thStatus')}</th>
                  <th className="px-5 py-3 font-semibold text-right">{t('dash.dropfans.thAction')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {accounts.map(a => (
                  <tr key={a.id} className="hover:bg-white/[0.02]" data-testid={`df-account-row-${a.id}`}>
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-2.5">
                        <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${a.color} flex items-center justify-center text-white text-xs font-bold`}>{a.initial}</div>
                        <span className="text-sm text-white">{a.name}</span>
                      </div>
                    </td>
                    <td className="px-5 py-3 text-sm text-slate-300 font-mono">{a.dropfans_profile ? `@${a.dropfans_profile}` : '—'}</td>
                    <td className="px-5 py-3">
                      {a.dropfans_enabled
                        ? <Badge className="bg-emerald-500/15 text-emerald-300 border-emerald-500/30"><CheckCircle2 size={10} className="mr-1" /> {t('dash.dropfans.statusEnabled')}</Badge>
                        : <Badge variant="outline" className="border-white/10 bg-white/5 text-slate-400">{t('dash.dropfans.statusDisabled')}</Badge>}
                    </td>
                    <td className="px-5 py-3 text-right">
                      {a.dropfans_enabled ? (
                        <Button data-testid={`df-edit-${a.id}`} size="sm" variant="outline" className="border-white/10 bg-white/5 text-slate-200 hover:bg-white/10 h-7 text-xs" onClick={() => setEnabling(a)}>
                          <User size={11} className="mr-1" /> {t('dash.dropfans.editProfile')}
                        </Button>
                      ) : (
                        <Button data-testid={`df-enable-${a.id}`} size="sm" className="bg-amber-500 hover:bg-amber-600 text-amber-950 h-7 text-xs" onClick={() => setEnabling(a)}>
                          <Zap size={11} className="mr-1" /> {t('dash.dropfans.enableDropfans')}
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <EnableDialog account={enabling} onClose={() => setEnabling(null)} onEnabled={load} />
        </TabsContent>

        <TabsContent value="api" className="space-y-4">
          <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5 space-y-4">
            <div className="flex items-center gap-2 text-white font-semibold text-sm"><KeyRound size={15} className="text-amber-400" /> {t('dash.dropfans.apiKey')}</div>
            <div className="flex gap-2">
              <Input data-testid="df-api-key-input" type="password" value={apiKey} onChange={e => setApiKey(e.target.value)} placeholder="dfk_live_..." className="bg-white/5 border-white/10 text-white h-10 font-mono flex-1" />
              <Button data-testid="df-save-api-key" className="bg-indigo-500 hover:bg-indigo-600 h-10" onClick={() => patchConfig({ api_key: apiKey }, t('dash.dropfans.apiKeySaved'))}>{t('dash.dropfans.save')}</Button>
            </div>
            <p className="text-xs text-slate-500">{t('dash.dropfans.apiKeyHelp')}</p>
          </div>

          <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5 space-y-4">
            <div className="flex items-center gap-2 text-white font-semibold text-sm"><Webhook size={15} className="text-indigo-400" /> {t('dash.dropfans.webhookEndpoint')}</div>
            <div className="flex gap-2 items-center">
              <code data-testid="df-webhook-url" className="flex-1 rounded-lg bg-black/40 border border-white/10 px-3 py-2.5 text-xs text-emerald-300 font-mono truncate">{webhookUrl}</code>
              <Button variant="outline" size="icon" className="border-white/10 bg-white/5 text-slate-300 h-10 w-10" onClick={() => copy(webhookUrl)}><Copy size={14} /></Button>
            </div>
            <p className="text-xs text-slate-500">{t('dash.dropfans.webhookHelp', { events: (config?.events || []).join(', ') })}</p>
            <div className="flex gap-2">
              <Input data-testid="df-webhook-secret-input" type="password" value={secret} onChange={e => setSecret(e.target.value)} placeholder="whsec_..." className="bg-white/5 border-white/10 text-white h-10 font-mono flex-1" />
              <Button data-testid="df-save-secret" className="bg-indigo-500 hover:bg-indigo-600 h-10" onClick={() => patchConfig({ webhook_secret: secret }, t('dash.dropfans.secretSaved'))}>{t('dash.dropfans.saveSecret')}</Button>
            </div>
            <p className="text-xs text-slate-500">{t('dash.dropfans.secretHelp')}</p>
          </div>

          <div className="rounded-2xl border border-white/10 bg-white/[0.02] overflow-hidden">
            <div className="px-5 py-3 border-b border-white/5 flex items-center justify-between">
              <span className="text-sm font-semibold text-white">{t('dash.dropfans.recentDeliveries')}</span>
              <span className="text-xs text-slate-500">{config?.last_event_at ? t('dash.dropfans.lastEvent', { date: fmtDateTime(config.last_event_at) }) : t('dash.dropfans.noEvents')}</span>
            </div>
            {deliveries.length === 0 ? (
              <div className="px-5 py-8 text-center text-sm text-slate-500">{t('dash.dropfans.noDeliveries')}</div>
            ) : (
              <table className="w-full">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-white/5">
                    <th className="px-5 py-2.5 font-semibold">{t('dash.dropfans.thEvent')}</th>
                    <th className="px-5 py-2.5 font-semibold">{t('dash.dropfans.thSignature')}</th>
                    <th className="px-5 py-2.5 font-semibold">{t('dash.dropfans.thProcessed')}</th>
                    <th className="px-5 py-2.5 font-semibold">{t('dash.dropfans.thReceived')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {deliveries.slice(0, 10).map(d => (
                    <tr key={d.id}>
                      <td className="px-5 py-2.5 text-sm text-white font-mono">{d.event_type}</td>
                      <td className="px-5 py-2.5">{d.signature_valid ? <Badge className="bg-emerald-500/15 text-emerald-300 border-emerald-500/30">{t('dash.dropfans.sigValid')}</Badge> : <Badge className="bg-rose-500/15 text-rose-300 border-rose-500/30">{t('dash.dropfans.sigInvalid')}</Badge>}</td>
                      <td className="px-5 py-2.5 text-sm text-slate-300">{d.processed ? t('dash.dropfans.yes') : t('dash.dropfans.no')}</td>
                      <td className="px-5 py-2.5 text-xs text-slate-500">{fmtDateTime(d.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </TabsContent>

        <TabsContent value="attribution" className="space-y-4">
          <div className="rounded-2xl border border-sky-500/20 bg-sky-500/[0.04] p-5">
            <div className="flex items-center gap-2 text-sm font-semibold text-sky-200 mb-3"><Info size={15} /> {t('dash.dropfans.attributionTitle')}</div>
            <ol className="list-decimal pl-5 space-y-1.5 text-[13px] text-slate-300">
              <li dangerouslySetInnerHTML={{ __html: t('dash.dropfans.attribution1') }} />
              <li dangerouslySetInnerHTML={{ __html: t('dash.dropfans.attribution2') }} />
              <li dangerouslySetInnerHTML={{ __html: t('dash.dropfans.attribution3') }} />
              <li dangerouslySetInnerHTML={{ __html: t('dash.dropfans.attribution4') }} />
            </ol>
            <div className="mt-3 pt-3 border-t border-white/10 text-[12px] text-slate-400 space-y-1.5">
              <p dangerouslySetInnerHTML={{ __html: t('dash.dropfans.attributionShift') }} />
              <p dangerouslySetInnerHTML={{ __html: t('dash.dropfans.attributionChatter') }} />
              <p dangerouslySetInnerHTML={{ __html: t('dash.dropfans.attributionNotif') }} />
            </div>
          </div>
          {/* Exemple visuel — pour comprendre d'un coup d'œil (partagé avec le guide) */}
          <SaleAttributionExample />
          <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5 space-y-5">
            <ToggleRow testid="df-toggle-auto" icon={Zap} title={t('dash.dropfans.toggleAutoTitle')} desc={t('dash.dropfans.toggleAutoDesc')}
              checked={!!config?.auto_attribution} onChange={v => patchConfig({ auto_attribution: v }, v ? t('dash.dropfans.autoEnabled') : t('dash.dropfans.autoDisabled'))} />
            <ToggleRow testid="df-toggle-approval" icon={ShieldCheck} title={t('dash.dropfans.toggleApprovalTitle')} desc={t('dash.dropfans.toggleApprovalDesc')}
              checked={!!config?.require_manager_approval} onChange={v => patchConfig({ require_manager_approval: v }, t('dash.dropfans.saved'))} />
          </div>
          <div className="rounded-2xl border border-white/10 bg-white/[0.02] overflow-hidden">
            <div className="px-5 py-3 border-b border-white/5 text-sm font-semibold text-white flex items-center justify-between gap-3">
              <span>{t('dash.dropfans.recentAttributions', { attributed, total: sales.length })}</span>
              <span className="text-[11px] font-normal text-sky-300/80">👉 Clique sur le nom du chatteur pour corriger une attribution</span>
            </div>
            <div className="max-h-[460px] overflow-y-auto scrollbar-thin">
              <table className="w-full">
                <thead className="sticky top-0 bg-[#0B0B0E] z-10">
                  <tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-white/5">
                    <th className="px-5 py-2.5 font-semibold">{t('dash.dropfans.thSale')}</th>
                    <th className="px-5 py-2.5 font-semibold">{t('dash.dropfans.thChatter')}</th>
                    <th className="px-5 py-2.5 font-semibold">{t('dash.dropfans.thReason')}</th>
                    <th className="px-5 py-2.5 font-semibold text-right">{t('dash.dropfans.thAmount')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {sales.slice(0, 100).map(s => (
                    <tr key={s.id} className="hover:bg-white/[0.02]">
                      <td className="px-5 py-2.5 text-sm text-white">{s.link || s.client || '—'}<div className="text-[10px] text-slate-600">{fmtDateTime(s.created_at)}</div></td>
                      <td className="px-5 py-2.5">
                        {estVenteBot(s) ? (
                          <span className="text-[11px] px-2 py-0.5 rounded-md bg-violet-500/15 text-violet-300 border border-violet-500/30 inline-flex items-center gap-1" data-testid={`sale-bot-${s.id}`} title="Vente du bot d'accueil : elle reste au Bot">🤖 Bot</span>
                        ) : (
                        <select
                          value={s.chatter_id || ''}
                          onChange={(e) => reassignSale(s.id, e.target.value)}
                          data-testid={`sale-chatter-${s.id}`}
                          className={`bg-[#101013] border rounded-lg px-2 py-1.5 text-xs outline-none cursor-pointer max-w-[170px] focus:border-sky-500/60 ${s.chatter_id ? 'border-white/10 text-slate-200' : 'border-amber-500/50 text-amber-300'}`}
                        >
                          <option value="">— à attribuer —</option>
                          {chatters.map(c => (<option key={c.id} value={c.id}>{c.name}</option>))}
                        </select>
                        )}
                      </td>
                      <td className="px-5 py-2.5"><code className="text-[10px] text-slate-500 font-mono">{s.reason}</code></td>
                      <td className="px-5 py-2.5 text-right text-sm font-bold text-emerald-400">{s.amount} €</td>
                    </tr>
                  ))}
                  {sales.length === 0 && <tr><td colSpan={4} className="px-5 py-10 text-center text-sm text-slate-500">Aucune vente pour l'instant.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="links">
          <div className="flex justify-between items-center mb-3 px-1">
            <p className="text-xs text-slate-500">{t('dash.dropfans.linksHelp')}</p>
          </div>
          <div className="rounded-2xl border border-white/10 bg-white/[0.02] overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-white/5">
                  <th className="px-5 py-3 font-semibold">{t('dash.dropfans.thLink')}</th>
                  <th className="px-5 py-3 font-semibold">{t('dash.dropfans.thAccount')}</th>
                  <th className="px-5 py-3 font-semibold">{t('dash.dropfans.thPrice')}</th>
                  <th className="px-5 py-3 font-semibold">{t('dash.dropfans.thSold')}</th>
                  <th className="px-5 py-3 font-semibold">{t('dash.dropfans.thStatus')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {links.map(l => {
                  const acc = accounts.find(a => a.id === l.account_id);
                  return (
                    <tr key={l.id} className="hover:bg-white/[0.02]" data-testid={`df-link-row-${l.id}`}>
                      <td className="px-5 py-3 text-sm text-white">{l.name}</td>
                      <td className="px-5 py-3 text-sm text-slate-300">{acc?.name || '—'}</td>
                      <td className="px-5 py-3 text-sm font-bold text-amber-400">{l.price} €</td>
                      <td className="px-5 py-3 text-sm text-slate-300">{l.sold}</td>
                      <td className="px-5 py-3">{l.active ? <Badge className="bg-emerald-500/15 text-emerald-300 border-emerald-500/30">{t('dash.dropfans.statusActive')}</Badge> : <Badge variant="outline" className="border-white/10 bg-white/5 text-slate-400">{t('dash.dropfans.statusPaused')}</Badge>}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Kpi({ icon: Icon, color, label, value, testid }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-4" data-testid={testid}>
      <Icon size={16} className={`${color} mb-2`} />
      <div className="text-xl font-bold text-white font-display">{value}</div>
      <div className="text-[11px] text-slate-500">{label}</div>
    </div>
  );
}

function ToggleRow({ icon: Icon, title, desc, checked, onChange, testid }) {
  return (
    <div className="flex items-start justify-between gap-4">
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-lg bg-white/5 flex items-center justify-center shrink-0"><Icon size={15} className="text-indigo-400" /></div>
        <div>
          <div className="text-sm font-semibold text-white mb-0.5">{title}</div>
          <div className="text-xs text-slate-400 max-w-xl">{desc}</div>
        </div>
      </div>
      <Switch data-testid={testid} checked={checked} onCheckedChange={onChange} />
    </div>
  );
}

function EnableDialog({ account, onClose, onEnabled }) {
  const { t } = useTranslation();
  const [profile, setProfile] = useState('');
  const [saving, setSaving] = useState(false);
  const isEdit = !!account?.dropfans_enabled;

  // Prefill with the current profile when re-opening to edit a linked account.
  useEffect(() => { setProfile(account?.dropfans_profile || ''); }, [account]);

  const enable = async () => {
    if (!profile.trim()) { toast.error(t('dash.dropfans.dialogProfileRequired')); return; }
    setSaving(true);
    try {
      await api.post(`/dropfans/accounts/${account.id}/enable`, null, { params: { dropfans_profile: profile.trim() } });
      toast.success(isEdit ? t('dash.dropfans.dialogProfileUpdated', { name: account.name }) : t('dash.dropfans.dialogEnabled', { name: account.name }));
      onClose();
      onEnabled();
    } catch (e) { toast.error(apiError(e, t('dash.dropfans.dialogError'))); } finally { setSaving(false); }
  };

  return (
    <Dialog open={!!account} onOpenChange={v => !v && onClose()}>
      <DialogContent className="bg-[#0B0B0E] border-white/10 text-slate-200">
        <DialogHeader><DialogTitle className="text-white">{isEdit ? t('dash.dropfans.dialogEditTitle') : t('dash.dropfans.enableDropfans')} — {account?.name}</DialogTitle></DialogHeader>
        <div className="space-y-3 pt-2">
          <div className="space-y-1.5">
            <Label className="text-slate-300 text-xs">{t('dash.dropfans.dialogProfileLabel')}</Label>
            <div className="relative">
              <User size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
              <Input data-testid="df-profile-input" value={profile} onChange={e => setProfile(e.target.value)} className="bg-white/5 border-white/10 text-white h-10 pl-8 font-mono" placeholder="moncompte" />
            </div>
          </div>
          <p className="text-xs text-slate-500" dangerouslySetInnerHTML={{ __html: t('dash.dropfans.dialogProfileHelp') }} />
          <Button data-testid="df-enable-confirm" className="w-full bg-amber-500 hover:bg-amber-600 text-amber-950" onClick={enable} disabled={saving}>{saving ? t('dash.dropfans.saving') : (isEdit ? t('dash.dropfans.enregistrer') : t('dash.dropfans.enable'))}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
